// /src/pages/Questionnaire.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaArrowRight,
  FaMapMarkerAlt,
  FaPlusCircle,
  FaCube,
  FaShieldAlt,
  FaUserCheck,
} from "react-icons/fa";

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

const fade = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.25 },
};

export default function Questionnaire() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // --- Resolve selected plan from state or query string --------------------
  const planIdFromQuery = params.get("plan");
  const planFromState = location.state?.plan;

  const PLAN_META = {
    advantage: { name: "Medicare Advantage Plans", icon: <FaPlusCircle className="text-[#e31c3d]" /> },
    partd: { name: "Part D Drug Plans", icon: <FaCube className="text-[#e31c3d]" /> },
    supplements: { name: "Medicare Supplements (Medigap)", icon: <FaShieldAlt className="text-[#e31c3d]" /> },
    term: { name: "Term Life Insurance", icon: <FaShieldAlt className="text-[#e31c3d]" /> },
    permanent: { name: "Permanent Life Insurance", icon: <FaUserCheck className="text-[#e31c3d]" /> },
  };

  const selectedMeta = useMemo(() => {
    if (planFromState?.id && PLAN_META[planFromState.id]) return PLAN_META[planFromState.id];
    if (planFromState?.name) return { name: planFromState.name };
    if (planIdFromQuery && PLAN_META[planIdFromQuery]) return PLAN_META[planIdFromQuery];
    return null;
  }, [planFromState, planIdFromQuery]);

  // --- ZIP chip (read-only, already captured earlier) ----------------------
  const userZip = typeof window !== "undefined" ? localStorage.getItem("userZipCode") : "";

  // --- Wizard config -------------------------------------------------------
  const steps = [
    {
      id: "partAB",
      title: "Do you have Medicare Part A & Part B?",
      description:
        "To qualify for a Medicare Advantage plan, you must be enrolled in both Part A and Part B.",
    },
    {
      id: "medicaid",
      title: "Do you have Medicaid?",
      description:
        "If you have Medicaid, you may qualify for a DSNP (Dual Special Needs Plan).",
    },
    {
      id: "partBHelp",
      title: "Are you getting help with your Part B premium?",
      description:
        "Some programs help pay your Medicare Part B premium. We'll note this if it applies.",
    },
    {
      id: "extraHelp",
      title: "Are you receiving extra help with your drug costs?",
      description:
        "Also called LIS — Low-Income Subsidy. This can lower your prescription costs.",
    },
    {
      id: "review",
      title: "Review your answers",
      description: "Confirm below and continue.",
    },
  ];

  const totalSteps = steps.length;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const cached = localStorage.getItem("medicareWizard");
    return cached
      ? JSON.parse(cached)
      : { partAB: null, medicaid: null, partBHelp: null, extraHelp: null, none: false };
  });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    localStorage.setItem("medicareWizard", JSON.stringify(answers));
  }, [answers]);

  const isReview = steps[step].id === "review";
  const blockedByPartAB = answers.partAB === false;
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  function handleSelect(id, value) {
    setAnswers(prev => ({ ...prev, [id]: value, none: false }));
    setTouched(true);
  }

  function handleNone() {
    setAnswers(prev => ({
      ...prev,
      medicaid: false,
      partBHelp: false,
      extraHelp: false,
      none: true,
    }));
    setStep(totalSteps - 1); // jump to review
  }

  function next() {
    const currentId = steps[step].id;
    if (currentId !== "review") {
      if (answers[currentId] === null) return setTouched(true);
      if (currentId === "partAB" && answers.partAB === false) {
        setTouched(true);
        return setStep(totalSteps - 1);
      }
    }
    setTouched(false);
    setStep(s => Math.min(totalSteps - 1, s + 1));
  }

  function back() {
    setTouched(false);
    setStep(s => Math.max(0, s - 1));
  }

  function resetAll() {
    setAnswers({ partAB: null, medicaid: null, partBHelp: null, extraHelp: null, none: false });
    setTouched(false);
    setStep(0);
  }

  function computeOutcome() {
    return {
      qualifiesMA: answers.partAB === true,
      dsnp: answers.medicaid === true,
      partBHelp: answers.partBHelp === true,
      lis: answers.extraHelp === true,
      noneOfThese: answers.none === true,
    };
  }

  // >>> Updated to send users to /matched-brokers WITH questionnaire in state <<<
  function finish() {
    const result = {
      answers,
      outcome: computeOutcome(),
      plan: planFromState?.id || planIdFromQuery || null,
      zip: userZip || null,
      ts: Date.now(),
      version: "q_v1",
    };
    localStorage.setItem("questionnaireResult", JSON.stringify(result));
    navigate("/matched-brokers", {
      state: { plan: result.plan, questionnaire: result },
    });
  }

  return (
    <div className="bg-[#f8fafc] text-[#002b5c] min-h-screen flex flex-col">
      <Helmet>
        <title>Enrollment Questionnaire | Your Choice Associates</title>
      </Helmet>

      <Navbar />

      <main className="flex-grow py-16 px-6">
        <div className="max-w-3xl mx-auto bg-white shadow rounded-2xl p-6 md:p-8 border border-[#002b5c]/10">
          {/* Header */}
          <motion.div {...fadeUp(0)} className="text-center">
            <span className="inline-block rounded-full bg-[#e8f1fb] px-3 py-1 text-xs font-semibold tracking-wider">
              Questionnaire
            </span>
            <h1 className="mt-3 text-3xl font-bold">Enrollment Questionnaire</h1>

            {/* chips */}
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              {selectedMeta?.name && (
                <span className="inline-flex items-center gap-2 rounded-full bg-[#fff1f2] text-[#b91c1c] px-3 py-1 text-sm font-semibold">
                  {selectedMeta.icon}
                  {selectedMeta.name}
                </span>
              )}
              {userZip && (
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm shadow-sm">
                  <FaMapMarkerAlt className="text-[#e31c3d]" />
                  ZIP <strong className="ml-1">{userZip}</strong>
                </span>
              )}
            </div>
          </motion.div>

          {/* Progress */}
          <div className="mt-6">
            <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-2 bg-[#002b5c] transition-[width] duration-300"
                style={{ width: `${progressPct}%` }}
                aria-hidden
              />
            </div>
            <div className="mt-2 text-xs text-gray-600 text-right">Step {step + 1} of {totalSteps}</div>
          </div>

          {/* Wizard Card */}
          <div className="mt-4 rounded-3xl bg-white">
            <AnimatePresence mode="wait">
              {!isReview && (
                <motion.div key={steps[step].id} {...fade}>
                  <h2 className="text-xl md:text-2xl font-semibold text-[#0f2437]">
                    {steps[step].title}
                  </h2>
                  <p className="mt-2 text-gray-600">{steps[step].description}</p>

                  {steps[step].id === "partAB" && answers.partAB === false && (
                    <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
                      <div className="font-semibold">You’ll need Part A & Part B for Medicare Advantage.</div>
                      <div className="text-sm mt-1">
                        You can still explore other coverage (Medigap or standalone Part D) or contact us for help enrolling in Original Medicare.
                      </div>
                    </div>
                  )}

                  <YesNo id={steps[step].id} value={answers[steps[step].id]} onSelect={handleSelect} touched={touched} />

                  {steps[step].id !== "partAB" && (
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={handleNone}
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
                          answers.none ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 hover:border-gray-500"
                        }`}
                      >
                        <span className="inline-block h-2 w-2 rounded-full bg-current" />
                        None of these apply
                      </button>
                      <div className="text-xs text-gray-500 mt-2">Mutually exclusive — selecting this clears other selections.</div>
                    </div>
                  )}

                  {steps[step].id === "medicaid" && answers.medicaid === true && (
                    <div className="mt-4 rounded-2xl border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
                      <div className="font-semibold">You may qualify for a DSNP plan.</div>
                      <div className="text-sm mt-1">Dual Special Needs Plans are for people with both Medicare and Medicaid.</div>
                    </div>
                  )}
                  {steps[step].id === "extraHelp" && answers.extraHelp === true && (
                    <div className="mt-4 rounded-2xl border border-sky-300 bg-sky-50 p-4 text-sky-900">
                      <div className="font-semibold">Extra Help (LIS) noted.</div>
                      <div className="text-sm mt-1">This may reduce your prescription copays and premiums on eligible plans.</div>
                    </div>
                  )}
                </motion.div>
              )}

              {isReview && (
                <motion.div key="review" {...fade}>
                  <h2 className="text-xl md:text-2xl font-semibold text-[#0f2437]">Review your answers</h2>
                  <p className="mt-2 text-gray-600">Here's a quick summary before you continue.</p>

                  <div className="mt-6 grid gap-3">
                    <SummaryRow label="Medicare Part A & Part B" value={asYesNo(answers.partAB)} />
                    <SummaryRow label="Medicaid" value={asYesNo(answers.medicaid, answers.none)} />
                    <SummaryRow label="Help with Part B premium" value={asYesNo(answers.partBHelp, answers.none)} />
                    <SummaryRow label="Extra Help with drug costs" value={asYesNo(answers.extraHelp, answers.none)} />
                    <SummaryRow label="None of these apply" value={answers.none ? "Selected" : "Not selected"} />
                  </div>

                  <div className="mt-6 rounded-2xl border bg-gray-50 p-4">
                    {blockedByPartAB ? (
                      <div>
                        <div className="font-semibold text-amber-800">You don't have Part A & B yet.</div>
                        <p className="text-sm text-amber-800/90 mt-1">
                          Medicare Advantage requires both. We can help you enroll in Original Medicare first or explore other options.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="font-semibold text-emerald-900">You're eligible to explore Medicare Advantage plans.</div>
                        <ul className="mt-2 text-sm text-emerald-900/90 list-disc ml-5 space-y-1">
                          {answers.medicaid && <li>DSNP pathway (Medicare + Medicaid)</li>}
                          {answers.partBHelp && <li>Receiving help with Part B premium</li>}
                          {answers.extraHelp && <li>Extra Help (LIS) for prescriptions</li>}
                          {answers.none && <li>None of the additional programs apply</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer Controls */}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <Link to="/plans" className="text-sm font-semibold text-[#0B5EFD]">Back to plans</Link>
              <div className="flex gap-3">
                {step > 0 && (
                  <button
                    onClick={back}
                    className="rounded-full border border-gray-300 px-5 py-2.5 text-sm hover:border-gray-500"
                    type="button"
                  >
                    Back
                  </button>
                )}

                {!isReview ? (
                  <button
                    onClick={next}
                    className="inline-flex items-center rounded-full bg-[#e31c3d] px-6 py-3 text-white font-semibold shadow hover:bg-[#c40030]"
                    type="button"
                  >
                    Next <FaArrowRight className="ml-2" />
                  </button>
                ) : (
                  <button
                    onClick={finish}
                    className="inline-flex items-center rounded-full bg-emerald-600 px-6 py-3 text-white font-semibold shadow hover:bg-emerald-700"
                    type="button"
                  >
                    Find Matched Brokers <FaArrowRight className="ml-2" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <p className="mt-6 text-xs text-gray-600 text-center">
            We only use your answers to match you with the right help programs and broker guidance.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function YesNo({ id, value, onSelect, touched }) {
  const showError = touched && value === null;
  return (
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => onSelect(id, true)}
        className={`w-full rounded-2xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
          value === true
            ? "border-[#002b5c] bg-[#002b5c] text-white"
            : "border-gray-300 bg-white hover:border-[#002b5c]"
        }`}
      >
        <div className="font-semibold">Yes</div>
        <div className="text-sm opacity-80">Select if this applies to you.</div>
      </button>
      <button
        type="button"
        onClick={() => onSelect(id, false)}
        className={`w-full rounded-2xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
          value === false
            ? "border-[#0f2437] bg-[#0f2437] text-white"
            : "border-gray-300 bg-white hover:border-[#0f2437]"
        }`}
      >
        <div className="font-semibold">No</div>
        <div className="text-sm opacity-80">Select if this does not apply.</div>
      </button>
      {showError && (
        <div className="col-span-full text-sm text-red-600">Please choose Yes or No to continue.</div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl border p-3 text-sm">
      <div className="font-medium text-[#0f2437]">{label}</div>
      <div className="text-gray-700">{value}</div>
    </div>
  );
}

function asYesNo(val, none = false) {
  if (none) return "No";
  if (val === true) return "Yes";
  if (val === false) return "No";
  return "—";
}
